import React from 'react';
import { Award, Users, BookOpen, Target, Linkedin, Globe } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  const aboutStructuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Vitta Finance Academy",
    "description": "Canada's #1 CIRO exam prep provider with 40+ years of combined instructor experience",
    "url": "https://www.vittafinance.ca/about",
    "foundingDate": "2010",
    "numberOfEmployees": "10-50",
    "alumni": {
      "@type": "QuantitativeValue",
      "value": "30000",
      "unitText": "successful students"
    },
    "member": [
      {
        "@type": "Person",
        "name": "Dr. Amina Beecroft",
        "jobTitle": "Lead Instructor, CFA, MBA, D.Fin",
        "sameAs": "https://www.linkedin.com/in/amina-beecroft/"
      },
      {
        "@type": "Person",
        "name": "Harry Vadalkar",
        "jobTitle": "Lead Instructor, CFA, CSC, MBA",
        "sameAs": "https://www.linkedin.com/in/herambvadalkar/"
      }
    ]
  };

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="About Vitta Finance Academy - Canada's #1 CIRO Exam Prep Provider"
        description="Learn about Vitta Finance Academy, trusted by 30,000+ learners. Meet expert instructors Dr. Amina Beecroft (CFA, MBA, D.Fin) and Harry Vadalkar (CFA, MBA) with 40+ years combined experience. Teaching at SAIT, MRU & TRU. Industry leading pass rate."
        keywords="Vitta Finance Academy, CIRO exam provider, Dr. Amina Beecroft, Harry Vadalkar, Canadian investment education, CIRO training, SAIT finance instructor, MRU finance instructor, TRU finance instructor, CIRO exam tutor Canada, best CIRO exam prep, top CIRO course provider"
        ogTitle="About Vitta Finance Academy - Expert CIRO Exam Preparation"
        ogDescription="Meet the team behind Canada's leading CIRO exam prep provider. 40+ years combined experience. 30,000+ successful students. Industry leading pass rate."
        structuredData={aboutStructuredData}
      />
      {/* Header Section */}
      <section style={{
        padding: '60px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Image */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <img 
            src="/images/backgrounds/about-hero.jpg"
            alt="Team collaboration"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.15
            }}
          />
        </div>
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <h1 className="brand-display" style={{ marginBottom: '24px', color: 'white' }}>
            About Vitta Finance
          </h1>
          <p className="body-large" style={{ 
            maxWidth: '800px',
            margin: '0 auto',
            color: 'rgba(255, 255, 255, 0.95)'
          }}>
            Canada's Leading CIRO Exam Prep Provider
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            <h2 className="heading-2" style={{ marginBottom: '32px' }}>
              Our Mission
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '24px',
              lineHeight: 1.8
            }}>
              At Vitta Finance Academy, we are dedicated to empowering aspiring financial professionals to achieve excellence in their CIRO certification journey. Our comprehensive programs cover the Canadian Investment Regulatory Exam (CIRE), Retail Securities Exam (RSE), Institutional Securities Exam (ISE), and Derivatives—equipping candidates with the knowledge, confidence, and mastery needed to succeed.
            </p>
            <p className="body-medium" style={{ lineHeight: 1.8, marginBottom: '24px' }}>
              Our learning ecosystem is built on a foundation of proven success, with over <strong>30,000 learners</strong> who have trusted our CSC, CFA, and IFC courses on platforms like Udemy, Skillshare, and YouTube. This track record has established Vitta Finance Academy as one of Canada's most trusted exam-prep providers.
            </p>
            <p className="body-medium" style={{ lineHeight: 1.8 }}>
              Our instructors bring <strong>40+ years of combined industry and teaching experience</strong> and currently teach at <strong>two leading Canadian universities</strong>, ensuring our curriculum reflects both academic rigor and real-world application.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section style={{
        padding: '60px 0',
        backgroundColor: 'var(--bg-card)'
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '24px',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            {[
              {
                icon: <Users size={28} color="var(--brand-primary)" />,
                stat: '30,000+',
                label: 'Successful Learners'
              },
              {
                icon: <Award size={28} color="var(--brand-primary)" />,
                stat: 'Industry Leading',
                label: 'Pass Rate'
              },
              {
                icon: <BookOpen size={28} color="var(--brand-primary)" />,
                stat: '40+',
                label: 'Years Experience'
              },
              {
                icon: <Target size={28} color="var(--brand-primary)" />,
                stat: '#1',
                label: 'CIRO Exam Prep'
              }
            ].map((item, index) => (
              <div key={index} style={{
                textAlign: 'center',
                padding: '24px 16px'
              }}>
                <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>
                  {item.icon}
                </div>
                <h3 style={{ 
                  marginBottom: '8px',
                  color: 'var(--brand-primary)',
                  fontSize: '1.75rem',
                  fontWeight: 800
                }}>
                  {item.stat}
                </h3>
                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)'
                }}>
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Authors Section */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--bg-card)'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Meet Our Instructors
          </h2>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '60px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {/* Dr. Amina Beecroft */}
            <div style={{
              padding: '40px',
              backgroundColor: 'var(--bg-page)',
              borderRadius: '12px',
              border: '1px solid var(--border-light)'
            }}>
              <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                <div style={{
                  width: '180px',
                  height: '180px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  flexShrink: 0,
                  backgroundColor: 'var(--bg-card)'
                }}>
                  <img 
                    src="/images/instructors/amina-beecroft.jpg"
                    alt="Dr. Amina Beecroft"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: '300px' }}>
                  <h3 className="heading-4" style={{ marginBottom: '8px' }}>
                    Dr. Amina Beecroft
                  </h3>
                  <p className="caption" style={{ marginBottom: '20px', color: 'var(--brand-primary)' }}>
                    CFA, MBA, D.Fin
                  </p>
                  <p className="body-small" style={{ lineHeight: 1.8, marginBottom: '16px' }}>
                    A <strong>seasoned finance executive</strong> with <strong>30+ years</strong> of experience across investment banking, portfolio management, and academia. Dr. Beecroft has held pivotal roles as a research analyst, buy-side analyst, and discretionary portfolio manager—bringing real-world market insights directly into the classroom.
                  </p>
                  <p className="body-small" style={{ lineHeight: 1.8, marginBottom: '20px' }}>
                    Her expertise in <strong>early-stage ventures</strong> and <strong>energy sector investments</strong> within private employee-owned firms gives students a unique perspective on both traditional and emerging markets. Known for her exceptional ability to translate complex financial concepts into actionable knowledge.
                  </p>
                  <a 
                    href="https://www.linkedin.com/in/amina-beecroft/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: 'var(--brand-primary)',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: '0.95rem'
                    }}
                  >
                    <Linkedin size={20} /> Connect on LinkedIn
                  </a>
                </div>
              </div>
            </div>

            {/* Harry Vadalkar */}
            <div style={{
              padding: '40px',
              backgroundColor: 'var(--bg-page)',
              borderRadius: '12px',
              border: '1px solid var(--border-light)'
            }}>
              <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                <div style={{
                  width: '180px',
                  height: '180px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  flexShrink: 0
                }}>
                  <img 
                    src="/images/instructors/harry-vadalkar.jpg"
                    alt="Harry Vadalkar"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: '300px' }}>
                  <h3 className="heading-4" style={{ marginBottom: '8px' }}>
                    Harry Vadalkar
                  </h3>
                  <p className="caption" style={{ marginBottom: '20px', color: 'var(--brand-primary)' }}>
                    CFA, CSC, MBA, Harvard and MIT Certified
                  </p>
                  <p className="body-small" style={{ lineHeight: 1.8, marginBottom: '16px' }}>
                    A <strong>visionary educator and fintech pioneer</strong> who has transformed how finance is taught. Harry has personally trained <strong>200,000+ investors</strong> and <strong>30,000+ finance professionals</strong>, delivering over <strong>55,000 hours</strong> of online education through his platform Edufinwizards. He currently teaches at <strong>SAIT, MRU, and TRU</strong>—three leading Canadian universities.
                  </p>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <p className="body-small" style={{ fontWeight: 600, marginBottom: '12px', color: 'var(--brand-primary)' }}>Career Highlights:</p>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      <li style={{ marginBottom: '10px', paddingLeft: '20px', position: 'relative', fontSize: '0.9rem', lineHeight: 1.6 }}>
                        <span style={{ position: 'absolute', left: 0, color: 'var(--gold-primary)' }}>✓</span>
                        <strong>Blockchain Pioneer:</strong> Co-founded BitBox, one of Canada's earliest Bitcoin trading companies; now leads AI-driven investment research at Phoenix Defi Investments
                      </li>
                      <li style={{ marginBottom: '10px', paddingLeft: '20px', position: 'relative', fontSize: '0.9rem', lineHeight: 1.6 }}>
                        <span style={{ position: 'absolute', left: 0, color: 'var(--gold-primary)' }}>✓</span>
                        <strong>Education Innovator:</strong> Created Canada's first Bachelor-level FinTech Major at SAIT; teaches CSC and IFIC certification courses
                      </li>
                      <li style={{ marginBottom: '10px', paddingLeft: '20px', position: 'relative', fontSize: '0.9rem', lineHeight: 1.6 }}>
                        <span style={{ position: 'absolute', left: 0, color: 'var(--gold-primary)' }}>✓</span>
                        <strong>TEDx Speaker:</strong> Delivered acclaimed talk on "AI, Blockchain, Compute, and Data – The ABCDs of the Future"
                      </li>
                      <li style={{ marginBottom: '10px', paddingLeft: '20px', position: 'relative', fontSize: '0.9rem', lineHeight: 1.6 }}>
                        <span style={{ position: 'absolute', left: 0, color: 'var(--gold-primary)' }}>✓</span>
                        <strong>Proven Results:</strong> Built 500+ hour e-learning repository; achieved 32% CAGR growth in education revenue
                      </li>
                    </ul>
                  </div>

                  <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                    <a 
                      href="https://www.linkedin.com/in/herambvadalkar/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'var(--brand-primary)',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.95rem'
                      }}
                    >
                      <Linkedin size={20} /> Connect on LinkedIn
                    </a>
                    <a 
                      href="https://www.harryvadalkar.ca" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'var(--brand-primary)',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.95rem'
                      }}
                    >
                      <Globe size={20} /> harryvadalkar.ca
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Our Expertise
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              'Finance and Investment Analysis',
              'Money and Banking',
              'Fintech and Innovation',
              'Risk Management & KYC',
              'Regulatory Compliance',
              'Wealth Management',
              'Portfolio Construction',
              'CIRO Regulations'
            ].map((item, index) => (
              <div key={index} style={{
                padding: '32px',
                backgroundColor: 'var(--bg-card)',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                transition: 'transform 0.3s ease'
              }} className="card-hover-effect">
                <h4 className="heading-5">{item}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--secondary-olive)'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Why Choose Vitta Finance Academy?
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {[
              {
                title: 'Exam-Focused Content',
                description: 'Every lesson, question, and mock exam is designed specifically for CIRO certification success.'
              },
              {
                title: 'Proven Track Record',
                description: '30,000+ successful students across our finance certification programs.'
              },
              {
                title: 'Expert Instructors',
                description: 'Learn from industry practitioners with decades of teaching and professional experience.'
              },
              {
                title: 'Comprehensive Materials',
                description: 'Videos, podcasts, mock exams, AI tutoring, and printed guides for every learning style.'
              },
              {
                title: 'Real-World Application',
                description: 'Case-based scenarios that prepare you for both the exam and your career.'
              },
              {
                title: '24/7 Support',
                description: 'AI tutor trained on CIRO syllabus available anytime you need help.'
              }
            ].map((item, index) => (
              <div key={index} style={{
                padding: '32px',
                backgroundColor: 'var(--bg-card)',
                borderRadius: '8px',
                border: '1px solid var(--border-medium)'
              }}>
                <h4 className="heading-5" style={{ marginBottom: '16px' }}>
                  {item.title}
                </h4>
                <p className="body-small">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--bg-card)'
      }}>
        <div className="container">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            padding: '40px',
            backgroundColor: 'var(--bg-page)',
            borderRadius: '8px',
            border: '1px solid var(--border-light)'
          }}>
            <h3 className="heading-4" style={{ marginBottom: '24px' }}>
              Important Note
            </h3>
            <p className="body-small" style={{ lineHeight: 1.7, marginBottom: '16px' }}>
              Vitta Finance Academy Inc. is independent and not affiliated with, endorsed by, or sponsored by CIRO, CIPF, or the Montreal Exchange. "CIRE" and related names are the property of their respective owners.
            </p>
            <p className="body-small" style={{ lineHeight: 1.7 }}>
              All course materials are provided for educational purposes only. While we strive for accuracy, materials may not reflect the most current rules. Always verify with official CIRO publications.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;